'use strict'

import {lotWeightUnitDisplayLabel} from '../upstreamContract'
import {formatWeight} from '@/util/refinery/weightDisplay'
import {convertWeightUnit} from '@/util/refinery/weightUtils'
import {
  computeSettlementPreview,
  resolveMetalRateTier,
  summariseManifest,
} from './math'

export const assessContractReadiness = (units, contract) => {
  const summary = summariseManifest(units)
  const paidMetals = Array.isArray(contract?.paidMetals)
    ? contract.paidMetals.map((metal) => String(metal).toLowerCase())
    : []
  const primaryMetals = Array.isArray(contract?.primaryMetals)
    ? contract.primaryMetals.map((metal) => String(metal).toLowerCase())
    : []
  const primarySet = new Set(primaryMetals)
  const lotWeight = assessLotWeight(summary.grossWeightToz, contract)
  const blockers = [...lotWeight.blockers]
  const warnings = []
  const perMetal = {}

  addPrimaryConfigurationBlockers({
    primaryMetals,
    paidMetals,
    summary,
    blockers,
    perMetal,
  })

  const qualifiers = qualifiersFromSummary(summary)
  const preview = computeSettlementPreview(contract || {}, summary, {
    reportedGrossToz: summary.grossWeightToz,
    perMetal: purityInputsFromSummary(summary, paidMetals),
  })
  paidMetals.forEach((metal) => {
    const role = primarySet.has(metal) ? 'primary' : 'secondary'
    const summaryRow = summary.byMetal?.[metal]
    const hasProjectedContent = Boolean(summaryRow && summaryRow.netPayableToz > 0)
    const tier = hasProjectedContent
      ? resolveMetalRateTier(contract, metal, {
          ...qualifiers,
          purityPct: summaryRow.weightedAvgPurityPct,
        })
      : null
    const minimumPayablePct = contract?.referenceFeeSchedule?.metalThresholds?.[metal]?.minimumPayablePct
    const failure = metalFailure({
      role,
      metal,
      summaryRow,
      tier,
      previewRow: preview[metal] || {},
      minimumPayablePct,
    })
    const relevantFailure = role === 'primary' || hasProjectedContent ? failure : null
    if (relevantFailure) {
      if (role === 'primary') blockers.push(relevantFailure)
      else warnings.push(relevantFailure)
    }
    perMetal[metal] = {
      role,
      hasProjectedContent,
      purityPct: summaryRow?.weightedAvgPurityPct ?? null,
      projectedFineToz: summaryRow?.netPayableToz ?? 0,
      creditedFineToz: preview[metal]?.creditedFineToz ?? null,
      minimumReturnToz: tier?.minimumReturnToz ?? null,
      minimumPayablePct: Number.isFinite(minimumPayablePct) ? minimumPayablePct : null,
      tier,
      passes: !relevantFailure,
      issues: relevantFailure ? [relevantFailure] : [],
    }
  })

  return {
    canConfirm: blockers.length === 0,
    lotWeight,
    perMetal,
    blockers,
    warnings,
  }
}

const requirementToToz = (requirement) => {
  if (!requirement || !Number.isFinite(requirement.value)) return null
  return convertWeightUnit(requirement.value, requirement.unit, 'TOZ')
}

const issue = (code, message, context = {}) => ({code, message, ...context})

const assessLotWeight = (grossWeightToz, contract) => {
  const requirements = (Array.isArray(contract?.lotWeightRequirements)
    ? contract.lotWeightRequirements
    : []
  ).map((requirement) => {
    const thresholdToz = requirementToToz(requirement)
    const passes = thresholdToz === null
      || (requirement.kind === 'minimum' && grossWeightToz >= thresholdToz)
      || (requirement.kind === 'maximum' && grossWeightToz <= thresholdToz)
      || (requirement.kind === 'guidance' && grossWeightToz >= thresholdToz)
    const blocks = requirement.enforcement === 'hard' && !passes
    return {...requirement, thresholdToz, passes, blocks}
  })
  const blockers = requirements
    .filter((requirement) => requirement.blocks)
    .map((requirement) => {
      const below = requirement.kind === 'minimum'
      return issue(
        below ? 'lot_weight_below_minimum' : 'lot_weight_above_maximum',
        `Lot weight must be ${below ? 'at least' : 'at most'} ${requirement.value} ${lotWeightUnitDisplayLabel(requirement.unit)} (${requirement.basis}).`,
        {requirement},
      )
    })
  return {grossWeightToz, requirements, blockers}
}

const purityInputsFromSummary = (summary, paidMetals) => {
  const perMetal = {}
  paidMetals.forEach((metal) => {
    const purityPct = summary.byMetal?.[metal]?.weightedAvgPurityPct
    if (Number.isFinite(purityPct)) perMetal[metal] = {purityPct}
  })
  return perMetal
}

const qualifiersFromSummary = (summary) => ({
  grossToz: summary.grossWeightToz,
  purityPctByMetal: Object.fromEntries(
    Object.entries(summary.byMetal || {}).map(([metal, result]) => [metal, result.weightedAvgPurityPct]),
  ),
})

const metalFailure = ({role, metal, summaryRow, tier, previewRow, minimumPayablePct}) => {
  const prefix = role === 'primary' ? 'primary' : 'secondary'
  if (!summaryRow || !Number.isFinite(summaryRow.netPayableToz) || summaryRow.netPayableToz <= 0) {
    return issue(`${prefix}_no_projected_content`, `${metal.toUpperCase()} has no projected content.`, {metal})
  }
  if (!tier) {
    return issue(`${prefix}_no_matching_tier`, `${metal.toUpperCase()} does not resolve to a contract rate tier.`, {metal})
  }
  if (Number.isFinite(tier.minimumReturnToz) && previewRow.creditedFineToz < tier.minimumReturnToz) {
    return issue(
      `${prefix}_below_minimum_return`,
      `${metal.toUpperCase()} credited fine is below the ${formatWeight(tier.minimumReturnToz, 'lot').formatted} minimum return.`,
      {metal},
    )
  }
  if (
    Number.isFinite(minimumPayablePct)
    && summaryRow.weightedAvgPurityPct < minimumPayablePct
  ) {
    return issue(
      `${prefix}_below_minimum_payable`,
      `${metal.toUpperCase()} content is below the ${minimumPayablePct}% payable threshold.`,
      {metal},
    )
  }
  return null
}

const addPrimaryConfigurationBlockers = ({
  primaryMetals,
  paidMetals,
  summary,
  blockers,
  perMetal,
}) => {
  if (primaryMetals.length === 0) {
    blockers.push(issue(
      'primary_metals_not_configured',
      'Primary metals are not configured for this contract.',
    ))
  }
  primaryMetals
    .filter((metal) => !paidMetals.includes(metal))
    .forEach((metal) => {
      const primaryNotPaidIssue = issue(
        'primary_not_paid',
        `${metal.toUpperCase()} is configured as primary but is not a paid metal.`,
        {metal},
      )
      blockers.push(primaryNotPaidIssue)
      perMetal[metal] = {
        role: 'primary',
        hasProjectedContent: Boolean(summary.byMetal?.[metal]?.netPayableToz > 0),
        purityPct: summary.byMetal?.[metal]?.weightedAvgPurityPct ?? null,
        projectedFineToz: summary.byMetal?.[metal]?.netPayableToz ?? 0,
        creditedFineToz: null,
        minimumReturnToz: null,
        minimumPayablePct: null,
        tier: null,
        passes: false,
        issues: [primaryNotPaidIssue],
      }
    })
}
